/**
 * IntervaloAgendamentoModal - Modal de alerta para violações de intervalo de agendamento
 *
 * Epic 46 - Controle de Intervalo de Agendamento por Cliente
 * Epic 47 - Sistema de Justificativas e Notificações
 *
 * Exibe violações de intervalo mínimo/máximo quando o usuário tenta agendar
 * uma contagem fora do intervalo configurado para os clientes.
 * A validação é informativa (não bloqueante) - o usuário pode continuar
 * MAS deve fornecer uma justificativa obrigatória.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AlertTriangle, Calendar, Clock, X, ChevronRight, FileText } from 'lucide-react';
import type { ValidarIntervaloResponse, ViolacaoIntervalo } from '../services/clienteService';

// Epic 47: Interface para justificativa por cliente
export interface JustificativaIntervalo {
  cliente_id: number;
  tipo_violacao: 'ABAIXO_MINIMO' | 'ACIMA_MAXIMO';
  justificativa: string;
  intervalo_configurado_dias: number;
  intervalo_atual_dias: number;
  data_ultima_contagem: string | null;
}

interface IntervaloAgendamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  // Epic 47: Nova callback que passa as justificativas
  onConfirmWithJustificativas?: (justificativas: JustificativaIntervalo[]) => void;
  validacoes: ValidarIntervaloResponse[];
  dataSelecionada: string;
  loading?: boolean;
}

const MIN_JUSTIFICATIVA_LENGTH = 10;

const IntervaloAgendamentoModal: React.FC<IntervaloAgendamentoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onConfirmWithJustificativas,
  validacoes,
  dataSelecionada,
  loading = false
}) => {
  // Epic 47: Estado para armazenar justificativas por cliente/violação
  const [justificativas, setJustificativas] = useState<Record<string, string>>({});

  // Filtrar apenas validações com violações
  const validacoesComViolacao = useMemo(() =>
    validacoes.filter(v => !v.valido && v.violacoes.length > 0),
    [validacoes]
  );

  // Epic 47: Gerar chave única para cada violação (cliente_id + tipo)
  const getJustificativaKey = useCallback((clienteId: number, tipo: string) =>
    `${clienteId}_${tipo}`,
    []
  );

  // Epic 47: Verificar se todas as justificativas estão preenchidas
  const allJustificativasValid = useMemo(() => {
    for (const validacao of validacoesComViolacao) {
      for (const violacao of validacao.violacoes) {
        const key = getJustificativaKey(
          validacao.cliente?.id || 0,
          violacao.tipo === 'INTERVALO_MINIMO' ? 'ABAIXO_MINIMO' : 'ACIMA_MAXIMO'
        );
        const justificativa = justificativas[key] || '';
        if (justificativa.trim().length < MIN_JUSTIFICATIVA_LENGTH) {
          return false;
        }
      }
    }
    return true;
  }, [justificativas, validacoesComViolacao, getJustificativaKey]);

  // Epic 47: Handler para mudança de justificativa
  const handleJustificativaChange = useCallback((
    clienteId: number,
    tipo: string,
    value: string
  ) => {
    const key = getJustificativaKey(clienteId, tipo);
    setJustificativas(prev => ({
      ...prev,
      [key]: value
    }));
  }, [getJustificativaKey]);

  // Epic 47: Handler para confirmar com justificativas
  const handleConfirm = useCallback(() => {
    if (onConfirmWithJustificativas) {
      const justificativasArray: JustificativaIntervalo[] = [];

      for (const validacao of validacoesComViolacao) {
        for (const violacao of validacao.violacoes) {
          const tipoViolacao = violacao.tipo === 'INTERVALO_MINIMO' ? 'ABAIXO_MINIMO' : 'ACIMA_MAXIMO';
          const key = getJustificativaKey(validacao.cliente?.id || 0, tipoViolacao);
          const justificativaText = justificativas[key] || '';

          justificativasArray.push({
            cliente_id: validacao.cliente?.id || 0,
            tipo_violacao: tipoViolacao,
            justificativa: justificativaText.trim(),
            intervalo_configurado_dias: violacao.intervalo_configurado,
            intervalo_atual_dias: violacao.intervalo_atual,
            data_ultima_contagem: violacao.ultima_contagem?.data_prevista || null,
          });
        }
      }

      onConfirmWithJustificativas(justificativasArray);
    } else {
      onConfirm();
    }
  }, [onConfirmWithJustificativas, onConfirm, validacoesComViolacao, justificativas, getJustificativaKey]);

  if (!isOpen) return null;

  // Formatar data para exibição
  const formatarData = (dataStr: string): string => {
    try {
      const data = parseISO(dataStr);
      return format(data, "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dataStr;
    }
  };

  // Obter ícone e cor baseado no tipo de violação
  const getViolacaoConfig = (tipo: ViolacaoIntervalo['tipo']) => {
    if (tipo === 'INTERVALO_MINIMO') {
      return {
        icon: <Clock className="w-4 h-4" />,
        bgColor: 'bg-orange-50 dark:bg-orange-900/20',
        borderColor: 'border-orange-200 dark:border-orange-800',
        textColor: 'text-orange-700 dark:text-orange-300',
        label: 'Intervalo mínimo não respeitado'
      };
    }
    return {
      icon: <Calendar className="w-4 h-4" />,
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
      textColor: 'text-red-700 dark:text-red-300',
      label: 'Intervalo máximo ultrapassado'
    };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20 rounded-t-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-800/30 rounded-full">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Alerta de Intervalo de Agendamento
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  A data selecionada não respeita o intervalo configurado
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-1 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Data selecionada */}
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Calendar className="w-4 h-4" />
              <span>Data selecionada:</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {formatarData(dataSelecionada)}
              </span>
            </div>
          </div>

          {/* Lista de violações por cliente */}
          <div className="space-y-4">
            {validacoesComViolacao.map((validacao, index) => (
              <div
                key={validacao.cliente?.id || index}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                {/* Cliente Header */}
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {validacao.cliente?.nome_fantasia || validacao.cliente?.razao_social || 'Cliente'}
                      </span>
                      {validacao.cliente?.codigo && (
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                          ({validacao.cliente.codigo})
                        </span>
                      )}
                    </div>
                    <span className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                      {validacao.violacoes.length} violação(ões)
                    </span>
                  </div>
                </div>

                {/* Violações */}
                <div className="p-4 space-y-3">
                  {validacao.violacoes.map((violacao, vIndex) => {
                    const config = getViolacaoConfig(violacao.tipo);
                    const tipoViolacao = violacao.tipo === 'INTERVALO_MINIMO' ? 'ABAIXO_MINIMO' : 'ACIMA_MAXIMO';
                    const key = getJustificativaKey(validacao.cliente?.id || 0, tipoViolacao);
                    const justificativa = justificativas[key] || '';
                    const isValid = justificativa.trim().length >= MIN_JUSTIFICATIVA_LENGTH;

                    return (
                      <div
                        key={vIndex}
                        className={`p-3 rounded-lg border ${config.bgColor} ${config.borderColor}`}
                      >
                        <div className={`flex items-center gap-2 font-medium ${config.textColor} mb-2`}>
                          {config.icon}
                          <span>{config.label}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Configurado:</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {violacao.intervalo_configurado} dias
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Atual:</span>
                            <span className={`font-medium ${config.textColor}`}>
                              {violacao.intervalo_atual} dias
                            </span>
                          </div>
                          {violacao.data_minima_permitida && (
                            <div className="flex justify-between col-span-2">
                              <span className="text-gray-600 dark:text-gray-400">Data mínima permitida:</span>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {formatarData(violacao.data_minima_permitida)}
                              </span>
                            </div>
                          )}
                          {violacao.data_maxima_permitida && (
                            <div className="flex justify-between col-span-2">
                              <span className="text-gray-600 dark:text-gray-400">Data máxima permitida:</span>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {formatarData(violacao.data_maxima_permitida)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Última contagem */}
                        {violacao.ultima_contagem && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                              Última contagem:
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <ChevronRight className="w-3 h-3 text-gray-400" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {formatarData(violacao.ultima_contagem.data_prevista)}
                              </span>
                              <span className="text-gray-400">-</span>
                              <span className="text-gray-600 dark:text-gray-400 truncate">
                                {violacao.ultima_contagem.titulo}
                              </span>
                              <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                {violacao.ultima_contagem.status_display}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Epic 47: Textarea para justificativa */}
                        {onConfirmWithJustificativas && (
                          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <FileText className="w-4 h-4" />
                              Justificativa
                              <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              value={justificativa}
                              onChange={(e) => handleJustificativaChange(
                                validacao.cliente?.id || 0,
                                tipoViolacao,
                                e.target.value
                              )}
                              placeholder="Descreva o motivo para agendar fora do prazo configurado..."
                              rows={3}
                              className={`
                                w-full px-3 py-2 text-sm rounded-lg
                                border ${isValid || justificativa.length === 0
                                  ? 'border-gray-300 dark:border-gray-600'
                                  : 'border-red-400 dark:border-red-600'
                                }
                                bg-white dark:bg-gray-800
                                text-gray-900 dark:text-gray-100
                                placeholder-gray-400 dark:placeholder-gray-500
                                focus:ring-2 focus:ring-amber-500 focus:border-amber-500
                                resize-none
                              `}
                              disabled={loading}
                            />
                            <div className="flex justify-between mt-1">
                              <span className={`text-xs ${
                                !isValid && justificativa.length > 0
                                  ? 'text-red-500'
                                  : 'text-gray-400 dark:text-gray-500'
                              }`}>
                                {!isValid && justificativa.length > 0 && justificativa.length < MIN_JUSTIFICATIVA_LENGTH
                                  ? `Mínimo ${MIN_JUSTIFICATIVA_LENGTH} caracteres`
                                  : 'Obrigatório'
                                }
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {justificativa.length} caracteres
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {onConfirmWithJustificativas
                ? 'Preencha as justificativas para continuar'
                : 'Deseja continuar mesmo assim?'
              }
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                         bg-white dark:bg-gray-800
                         border border-gray-300 dark:border-gray-600
                         rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700
                         disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading || (onConfirmWithJustificativas && !allJustificativasValid)}
                className="px-4 py-2 text-sm font-medium text-white
                         bg-amber-600 hover:bg-amber-700
                         rounded-lg
                         disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center gap-2"
                title={
                  onConfirmWithJustificativas && !allJustificativasValid
                    ? 'Preencha todas as justificativas com no mínimo 10 caracteres'
                    : undefined
                }
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Salvando...
                  </>
                ) : (
                  'Continuar Mesmo Assim'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntervaloAgendamentoModal;
